import { Info } from 'lucide-react';
import { formatMoney, formatNumber, formatPercent } from '@blimo/shared';
import { Alert, AlertDescription, AlertTitle } from '../components/alert.jsx';
import { EmptyState } from '../components/empty-state.jsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/table.jsx';
import { TrendChart } from './trend-chart.jsx';

/**
 * Accumulated development per year since the purchase (GET /portal/properties/{id}/accumulated-development).
 * `available = false` → explanation (purchase month/price missing) with an optional action.
 * @param {{ data: any, action?: import('react').ReactNode }} props
 */
export function AccumulatedDevelopmentView({ data, action }) {
  if (!data?.available) {
    return (
      <Alert variant="info">
        <Info aria-hidden="true" />
        <div className="flex flex-col gap-2">
          <AlertTitle>Kumulierte Entwicklung nicht verfügbar</AlertTitle>
          <AlertDescription>Kaufdatum/Kaufpreis fehlt – in den Einstellungen der Liegenschaft hinterlegen.</AlertDescription>
          {action}
        </div>
      </Alert>
    );
  }
  const years = data.years ?? [];
  if (years.length === 0) return <EmptyState compact title="Noch keine Jahreswerte" />;
  const rows = years.map((y) => ({ ...y, label: String(y.year) }));
  return (
    <div className="flex flex-col gap-3">
      <TrendChart
        title="Einnahmen und Kosten kumuliert"
        description="seit dem Kaufjahr, Kosten inkl. Kosten beim Kauf"
        data={rows}
        series={[
          { key: 'cumulative_income', label: 'Einnahmen kumuliert', color: 'var(--chart-1)' },
          { key: 'cumulative_costs', label: 'Kosten kumuliert', color: 'var(--chart-2)' },
        ]}
        formatValue={(v) => formatMoney(v, { fractionDigits: 0 })}
        formatAxis={(v) => `${formatNumber(v / 1000, { maximumFractionDigits: 0 })} T€`}
        height={200}
      />
      <Table aria-label="Kumulierte Entwicklung je Jahr" containerClassName="rounded-md border">
        <TableHeader>
          <TableRow>
            <TableHead>Jahr</TableHead>
            <TableHead data-numeric="true">Einnahmen</TableHead>
            <TableHead data-numeric="true">Kosten</TableHead>
            <TableHead data-numeric="true">Einnahmen kumuliert</TableHead>
            <TableHead data-numeric="true">Kosten kumuliert</TableHead>
            <TableHead data-numeric="true">Rendite kumuliert</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {years.map((y) => (
            <TableRow key={y.year}>
              <TableCell className="numeric">{y.year}</TableCell>
              <TableCell data-numeric="true">{formatMoney(y.income)}</TableCell>
              <TableCell data-numeric="true">{formatMoney(y.costs)}</TableCell>
              <TableCell data-numeric="true">{formatMoney(y.cumulative_income)}</TableCell>
              <TableCell data-numeric="true">{formatMoney(y.cumulative_costs)}</TableCell>
              <TableCell data-numeric="true" className="font-semibold">
                {formatPercent(y.yield)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <p className="text-xs text-muted-foreground">Rendite kumuliert = (Einnahmen kumuliert − Kosten kumuliert + Mieteinnahmen beim Kauf) / Kaufpreis.</p>
    </div>
  );
}
